import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { format, formatDistanceToNow } from 'date-fns'
import { FileText } from 'lucide-react'
import React from 'react'

import TanstackTable from 'components/reusable/table/TanstackTable'
import { CollapsibleContainer, IconButton } from 'slab'

import { MatchReceiptButton } from './MatchReceiptButton'
import { type UnmatchedReceipt } from './types'

interface UnmatchedReceiptsTableProps {
  unmatchedReceipts: UnmatchedReceipt[]
  substantiationId: string
  setOpen: boolean
}

const columnHelper = createColumnHelper<UnmatchedReceipt>()

const getColumns = (substantiationId: string) => [
  columnHelper.accessor('message_created_at', {
    header: 'Submitted At',
    cell: (info) => {
      const date = new Date(info.getValue())
      return (
        <div>
          <div>{format(date, 'M/d/yyyy')}</div>
          <div className='text-xs text-gray-500'>{formatDistanceToNow(date, { addSuffix: true })}</div>
        </div>
      )
    },
  }),
  columnHelper.accessor('taggun_result', {
    header: 'Amount',
    id: 'amount',
    cell: (info) => {
      const amount = info.getValue()?.totalAmount?.data
      return amount ? `$${amount.toFixed(2)}` : '-'
    },
  }),
  columnHelper.accessor('taggun_result', {
    header: 'Merchant',
    id: 'merchant',
    cell: (info) => {
      const merchant = info.getValue()?.merchantName?.data
      return merchant || '-'
    },
  }),
  columnHelper.display({
    id: 'actions',
    header: 'Actions',
    cell: (info) => {
      return (
        <div className='flex gap-2'>
          <IconButton
            icon={FileText}
            label='View Receipt'
            title='View Receipt'
            onClick={() => {
              if (info.row.original.attachment_url) {
                window.open(info.row.original.attachment_url, '_blank', 'noopener,noreferrer')
              }
            }}
          />
          <MatchReceiptButton substantiationId={substantiationId} blobId={info.row.original.blob_id} />
        </div>
      )
    },
  }),
]

const UnmatchedReceiptsTableImpl: React.FC<UnmatchedReceiptsTableProps> = ({ unmatchedReceipts, substantiationId, setOpen }) => {
  const table = useReactTable({
    data: unmatchedReceipts,
    columns: getColumns(substantiationId),
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: 5,
      },
    },
  })

  return (
    <CollapsibleContainer heading='Unmatched Receipts' defaultOpen={setOpen}>
      <div className='px-4 mt-4'>
        <TanstackTable
          table={table}
          paginationLabelSingular='receipt'
        />
      </div>
    </CollapsibleContainer>
  )
}

const UnmatchedReceiptsTable: React.FC<UnmatchedReceiptsTableProps> = (props) => {
  return (
    <UnmatchedReceiptsTableImpl {...props} />
  )
}

export default UnmatchedReceiptsTable
